import React, { Component, createRef } from "react"
import ReactDOMServer from "react-dom/server"
import DraggableWindow from "../DraggableWindow"
import "./index.css"

interface Props {
    enabled: boolean
    onHighlight?: (highlightedHTML) => void
    children?: React.ReactNode
}

interface State {
    highlightedHTML: string
    showToolbar: boolean
    toolbarX: number
    toolbarY: number
    range: Range | null
}

export default class Highlighter extends Component<Props, State> {
    contentRef = createRef<HTMLDivElement>()
    allowToolbar = true

    constructor(props: Props) {
        super(props)
        this.handleMouseUp = this.handleMouseUp.bind(this)
        this.applyHighlight = this.applyHighlight.bind(this)
        this.state = {
            highlightedHTML: "",
            showToolbar: false,
            toolbarX: 0,
            toolbarY: 0,
            range: null,
        }
    }

    componentDidMount() {
        this.setInitialHTML()
    }

    componentDidUpdate(prevProps: Props) {
        if (prevProps.children !== this.props.children) {
            let html = ReactDOMServer.renderToStaticMarkup(this.props.children as React.ReactElement)
            this.setState({highlightedHTML: html})
        }
    }

    setInitialHTML() {
        if (this.contentRef.current) {
            let html = ReactDOMServer.renderToStaticMarkup(this.props.children as React.ReactElement)
            this.setState({highlightedHTML: html})
        }
    }

    handleMouseUp(e: MouseEvent | React.MouseEvent) {
        let {enabled} = this.props

        if (!enabled) return

        let selection = window.getSelection()

        if (!selection || selection.rangeCount === 0) return

        let range = selection.getRangeAt(0)
        let selectedText = selection.toString().trim()

        if (!selectedText) {
            this.setState({showToolbar: false})
            return
        }

        if (!this.contentRef.current?.contains(range.commonAncestorContainer)) {
            this.setState({showToolbar: false})
            return
        }

        this.setState({
            showToolbar:  this.allowToolbar ? true: false,
            toolbarX: e.clientX - 30,
            toolbarY: e.clientY - 60,
            range,
        })
    }

    applyHighlight(color: string) {
        let { range } = this.state

        if (!range) return

        let span = document.createElement("span")
        span.style.backgroundColor = color

        let selectedContents = range.extractContents()
        span.appendChild(selectedContents)
        range.insertNode(span)

        window.getSelection()?.removeAllRanges()
        this.updateContent()
        this.setState({ showToolbar: false, range: null })
    }

    applyUnderline = () => {
        let { range } = this.state
        if (!range) return

        let contents = range.extractContents()
        let wrapper = document.createElement("span")

        wrapper.style.textDecoration = "underline"
        wrapper.appendChild(contents)
        range.insertNode(wrapper)

        window.getSelection()?.removeAllRanges()
        this.updateContent()
        this.setState({showToolbar: false, range: null})
    }

    removeHighlight = () => {
        let {onHighlight, enabled} = this.props

        if (!enabled) return

        if (onHighlight) {
            onHighlight("")
        }

        this.setState({showToolbar: false, range: null})
    }

    updateContent = () => {
        let {onHighlight, enabled} = this.props

        if (!enabled) return

        let highlightedHTML = this.contentRef.current?.innerHTML || ""
        this.setState({ highlightedHTML })

        if (onHighlight) {
            onHighlight(highlightedHTML)
        }
    }

    renderToolbar = () => {
        let { showToolbar, toolbarX, toolbarY } = this.state
        let { enabled } = this.props

        if (!enabled || !showToolbar) return null

        let colors = ["lightyellow", "lightblue", "lightpink"]

        return (
            <div className="highlighter-toolbar" style={{left: toolbarX, top: toolbarY}}>
                {colors.map((color) => (
                    <button
                        className="highlight-btn"
                        key={color}
                        onClick={() => this.applyHighlight(color)}
                        style={{backgroundColor: color}}
                    />
                ))}

                <button className="highlighter-toolbar-underline-btn" onClick={this.applyUnderline}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" 
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M6 4v6a6 6 0 0 0 12 0V4"/>
                        <line x1="4" x2="20" y1="20" y2="20"/>
                    </svg>
                </button>

                <button className="highlighter-toolbar-delete-btn" onClick={this.removeHighlight} >
                    <svg xmlns="http://www.w3.org/2000/svg" 
                        width="24" height="24" viewBox="0 0 24 24" fill="none" 
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M3 6h18"/>
                        <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/>
                        <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/>
                        <line x1="10" x2="10" y1="11" y2="17"/>
                        <line x1="14" x2="14" y1="11" y2="17"/>
                    </svg>
                </button>
            </div>
        )
    }

    render() {
        let {highlightedHTML} = this.state
        let {enabled} = this.props

        return (
            <div onMouseUp={this.handleMouseUp}>
                <div className="highlighter-content"
                    ref={this.contentRef}
                    contentEditable={enabled}
                    spellCheck={false}
                    suppressContentEditableWarning
                    onBeforeInput={(e) => e.preventDefault()}
                    onKeyDown={(e) => e.preventDefault()}
                    onPaste={(e) => e.preventDefault()}
                >
                    <div dangerouslySetInnerHTML={{__html: highlightedHTML}}/>
                </div>

                {this.renderToolbar()}
            </div>
        )
    }
}


//--- Highlighter for ACT ---
/**
 * Highlighter for ACT
 */
export class HighlighterACT extends Highlighter {
    constructor(props) {
        super(props)
        this.allowToolbar = false
    }

    handleMouseUp(e: MouseEvent | React.MouseEvent) {
        super.handleMouseUp(e)
        setTimeout(() => {
            this.applyHighlight("#87ceeb")
        }, 100)
    }
}

interface IHighlightControlsACTProps {
    draggable: boolean
    customClass: string
    sizes?: string[]
    onClose: () => void
    onHelp: () => void
    onAllowEdit: () => void
    onClear: () => void
}

interface IHighlightControlsACTState {

}

/**
 * The controllers for the ACT highlighter
 */
export class HighlightControlsACT extends Component<IHighlightControlsACTProps, IHighlightControlsACTState> {
    render() {
        let {draggable=true, customClass, sizes=["act-highlighter-small", "w-100"],
            onAllowEdit, onClear, onClose, onHelp} = this.props
        let showHelpButton = false // OT: For now we are not showing help button!

        return (
            <DraggableWindow title="Highlighter" 
                draggable={draggable} 
                sizes={sizes} 
                maximized={false} 
                showMaximizeButton={false}
                showHelpButton={showHelpButton}
                onClose={onClose} 
                customClass={customClass}
                onHelp={onHelp}>
                    <div className="act-highlighter-controls">
                        <button className="eact-btn btn-edit primary" onClick={onAllowEdit}>EDIT</button>
                        <button className="eact-btn alt btn-clear" onClick={onClear}>CLEAR</button>
                    </div>
            </DraggableWindow>
        )
    }
}
